import {
  AuthorizationError,
  AuthorizationException,
  ConnectionError,
  ConnectionException,
  CoreException,
  Failure,
  InvalidOrganizationError,
  InvalidOrganizationException,
  InvalidTokenError,
  InvalidTokenException,
  LimitExceededError,
  LimitExceededException,
  Response,
  Success,
  UnknownError
} from '@sdk/sdk';

import { Injectable } from '@angular/core';
import { AssistantAuthorization, CliAuthorizationRequest, Organization } from '../../auth';
import { AssistantDevicesRepository } from '../repositories/assistant-devices.repository';

@Injectable({ providedIn: 'root' })
export class AuthorizeAssistantUseCase {
  constructor(private _repository: AssistantDevicesRepository) {}

  async execute(
    organization: Organization,
    request: CliAuthorizationRequest
  ): Promise<Response<AssistantAuthorization, AuthorizeAssistantError>> {
    const result = await this._repository.authorize(organization, request);
    if (result instanceof Failure) {
      const error = this._exceptionToError(result.error);
      return new Failure(error);
    }
    return new Success(result.value);
  }

  private _exceptionToError(exception: CoreException): AuthorizeAssistantError {
    if (exception instanceof InvalidTokenException) {
      return new AuthorizeAssistantSessionExpiredError();
    }
    if (exception instanceof AuthorizationException) {
      return new AuthorizeAssistantForbiddenError();
    }
    if (exception instanceof LimitExceededException) {
      return new AuthorizeAssistantRateLimitedError();
    }
    if (exception instanceof ConnectionException) {
      return new AuthorizeAssistantConnectionError();
    }
    if (exception instanceof InvalidOrganizationException) {
      return new AuthorizeAssistantUntrustedInstanceError();
    }
    return new UnknownError();
  }
}

export abstract class AuthorizeAssistantError {}

export class AuthorizeAssistantSessionExpiredError
  extends InvalidTokenError
  implements AuthorizeAssistantError {}
export class AuthorizeAssistantForbiddenError extends AuthorizationError implements AuthorizeAssistantError {}
export class AuthorizeAssistantRateLimitedError
  extends LimitExceededError
  implements AuthorizeAssistantError {}
export class AuthorizeAssistantConnectionError extends ConnectionError implements AuthorizeAssistantError {}
/** The organization's instance is not a Sytex host: retrying cannot help. */
export class AuthorizeAssistantUntrustedInstanceError
  extends InvalidOrganizationError
  implements AuthorizeAssistantError {}
